import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { HandCoins, Plus } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { money } from '@/lib/utils';
import { Button, Card, EmptyState, PageHeader, Skeleton } from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { LOAN_STATUS } from '@/lib/status';

interface Loan {
  id: string;
  loanNumber: string;
  productName: string;
  status: string;
  principal: string;
  totalRepayable: string;
  amountRepaid: string;
  outstanding: string;
  progress: number;
  daysInArrears: number;
  nextPayment: { dueDate: string; amount: string } | null;
}

export default function MyLoans() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['my-loans'],
    queryFn: () => api.get<Loan[]>('/loans/my'),
  });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader
        eyebrow="Interest-free borrowing"
        title="My loans"
        actions={
          <Link to="/portal/loans/apply">
            <Button>
              <Plus size={16} /> Apply for a loan
            </Button>
          </Link>
        }
      />

      {data.length === 0 ? (
        <EmptyState
          icon={<HandCoins size={20} />}
          title="No loans yet"
          description="Apply for an interest-free AAF-UNIFEMGA loan and it will appear here with its full repayment schedule."
          action={
            <Link to="/portal/loans/apply">
              <Button>Apply for a loan</Button>
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((loan) => (
            <Link key={loan.id} to={`/portal/loans/${loan.id}`}>
              <Card className="p-5 transition-shadow hover:shadow-md">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="numeric font-heading font-bold text-foreground">
                      {loan.loanNumber}
                    </p>
                    <p className="text-sm text-foreground-secondary">{loan.productName}</p>
                  </div>
                  <StatusBadge map={LOAN_STATUS} status={loan.status} />
                </div>

                <div className="mb-4 flex items-end justify-between gap-4 rounded-lg bg-muted px-4 py-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-secondary">
                      Loan amount
                    </p>
                    <p className="numeric font-semibold text-foreground-secondary">
                      {money(loan.principal)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-secondary">
                      Outstanding
                    </p>
                    <p className="numeric text-lg font-bold leading-none text-foreground">
                      {money(loan.outstanding)}
                    </p>
                  </div>
                </div>

                <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-success transition-all"
                    style={{ width: `${Math.round(loan.progress * 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-foreground-secondary">
                  <span className="numeric">{money(loan.amountRepaid)} repaid</span>
                  <span className="numeric font-semibold text-foreground">
                    {money(loan.outstanding)} left
                  </span>
                </div>

                {loan.nextPayment && (
                  <p className="mt-3 border-t border-border pt-3 text-xs text-foreground-secondary">
                    Next:{' '}
                    <span className="numeric font-semibold text-foreground">
                      {money(loan.nextPayment.amount)}
                    </span>{' '}
                    on {formatLongDate(new Date(loan.nextPayment.dueDate))}
                  </p>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
